import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import { getUserRole } from "../../redux";
import { useUserHookFormList } from "../../components/UsersHookFormSelect";
import { PERMISSIONS_KEY, PERMISSION_LEVEL_KEYS } from "../../constants/permissions";
import { ADMIN_ROLE_IDS } from "../../constants/roles";
import { useHubSnackBar } from "../useSnackBar";

const STORAGE_KEY = "workorder_scanned_employee";

// Create the context
const EmployeeScanContext = createContext();

// Provider component
export const EmployeeScanContextProviderComponent = ({ 
  children, 
  workOrderId,
  locationId,
  userRole 
}) => {
  const [scannedEmployee, setScannedEmployee] = useState(null);

  // Get user list for employee lookup
  const { users } = useUserHookFormList({ locationId });
  const { addSnackbarSuccess, addSnackbarError } = useHubSnackBar();

  // Load from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Only restore if it matches the current work order
        if (parsed.workOrderId === workOrderId) {
          setScannedEmployee(parsed);
        }
      }
    } catch (error) {
      console.error("Failed to load scanned employee from session:", error);
    }
  }, [workOrderId]);

  // Save to sessionStorage whenever it changes
  useEffect(() => {
    try {
      if (scannedEmployee) {
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(scannedEmployee));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch (error) {
      console.error("Failed to save scanned employee to session:", error);
    }
  }, [scannedEmployee]);

  // Clear session on unmount (navigation away)
  useEffect(() => {
    return () => {
      sessionStorage.removeItem(STORAGE_KEY);
    };
  }, []);

  const scanEmployee = useCallback((employee) => {
    if (!employee || !employee.id) return;

    const sessionData = {
      userId: employee.id,
      employeeId: employee.employeeId,
      name: employee.name,
      roleId: employee.roleId,
      isSupervisor: employee.isSupervisor || false,
      workOrderId,
      scannedAt: new Date().toISOString(),
    };

    setScannedEmployee(sessionData);
  }, [workOrderId]);

  const clearSession = useCallback(() => {
    setScannedEmployee(null);
  }, []);

  const canEditLabor = useCallback((laborTicketUserId) => {
    // Owner and Admin roles (both location sets) can edit all tickets without scanning
    if (ADMIN_ROLE_IDS.has(userRole)) return true;
    
    // Otherwise, must be scanned in
    if (!scannedEmployee) return false;
    
    // Supervisors can edit all tickets
    if (scannedEmployee.isSupervisor) return true;
    
    // Regular employees can only edit their own tickets
    return scannedEmployee.userId === laborTicketUserId;
  }, [scannedEmployee, userRole]);

  // Handle employee ID input
  const handleEmployeeScan = useCallback((employeeId) => {
    const user = users?.find((u) => u?.employeeId === employeeId);

    if (user?.id) {
      // Check if the user has ADMIN_LABOR permission (production supervisor)
      const adminLaborPermission = user?.role?.permissions?.find(
        (p) => p.permissionId === PERMISSIONS_KEY.ADMIN_LABOR
      );
      const isSupervisor = adminLaborPermission?.value >= PERMISSION_LEVEL_KEYS.VIEW_ONLY;

      scanEmployee({ ...user, isSupervisor });
      addSnackbarSuccess({ message: "Access Successfully Elevated" });
    } else {
      addSnackbarError({ message: "User not found" });
    }
  }, [users, scanEmployee, addSnackbarSuccess, addSnackbarError]);

  // Hide scan indicator for Owners and Admins who already have permission to edit everything
  const shouldShowScan = !ADMIN_ROLE_IDS.has(userRole);

  return (
    <EmployeeScanContext.Provider
      value={{
        scannedEmployee,
        isScanned: !!scannedEmployee,
        scanEmployee,
        clearSession,
        canEditLabor,
        handleEmployeeScan,
        shouldShowScan,
      }}
    >
      {children}
    </EmployeeScanContext.Provider>
  );
};

// Connect to Redux to get userRole
const mapStateToProps = (state) => ({
  userRole: getUserRole(state),
});

export const EmployeeScanContextProvider = connect(mapStateToProps)(EmployeeScanContextProviderComponent);

// Hook to use the context
export const useEmployeeScanContext = () => {
  const context = useContext(EmployeeScanContext);
  if (!context) {
    throw new Error(
      "useEmployeeScanContext must be used within an EmployeeScanContextProvider"
    );
  }
  return context;
};
